import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import type {
  EmotionalResidue,
  LoopFrequency,
  OutcomeType,
  RehearsalDraft,
  RuminationTheme,
} from '../lib/types';
import {
  RUMINATION_LABELS,
  FREQUENCY_LABELS,
  OUTCOME_LABELS,
} from '../lib/types';
import { getDraft, saveDraft, clearDraft, saveRehearsalLocally, syncRehearsalToServer } from '../lib/store';
import { getWorkspace } from '../lib/store';

interface Props {
  onComplete: (rehearsalId: string) => void;
  onExit: () => void;
}

type Step = 'gratitude' | 'rumination' | 'stage' | 'outcome' | 'measures' | 'review';

const STEPS: Step[] = ['gratitude', 'rumination', 'stage', 'outcome', 'measures', 'review'];

const INITIAL_RESIDUE: EmotionalResidue = { shame: 5, anxiety: 6, fatigue: 7, self_doubt: 6 };

export function RehearsalStage({ onComplete, onExit }: Props) {
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];

  const [draft, setDraft] = useState<RehearsalDraft>(() => {
    const saved = getDraft();
    return {
      emotional_residue_before: { ...INITIAL_RESIDUE, ...saved.emotional_residue_before },
      emotional_residue_after: { ...INITIAL_RESIDUE, ...saved.emotional_residue_after },
      ...saved,
    };
  });

  const [startTime] = useState(Date.now());
  const [isSaving, setIsSaving] = useState(false);
  const [localOnly, setLocalOnly] = useState(true); // default safe

  const ws = getWorkspace();
  const consent = ws.consent;

  function updateDraft(patch: Partial<RehearsalDraft>) {
    const next = { ...draft, ...patch };
    setDraft(next);
    saveDraft(next);
  }

  function next() {
    if (stepIndex < STEPS.length - 1) setStepIndex(stepIndex + 1);
  }
  function back() {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  }

  const canAdvance =
    (step === 'gratitude' && (draft.gratitude || '').trim().length > 12) ||
    (step === 'rumination' && !!draft.rumination_theme) ||
    (step === 'stage') ||
    (step === 'outcome' && !!draft.outcome_type && (draft.outcome_text || '').trim().length > 6) ||
    (step === 'measures' && typeof draft.tolerance_after === 'number' && typeof draft.reappraisal_success === 'number');

  async function finishAndSave(alsoServer: boolean) {
    setIsSaving(true);

    const duration = Date.now() - startTime;

    const fullResBefore: EmotionalResidue = {
      shame: draft.emotional_residue_before?.shame ?? 5,
      anxiety: draft.emotional_residue_before?.anxiety ?? 6,
      fatigue: draft.emotional_residue_before?.fatigue ?? 7,
      self_doubt: draft.emotional_residue_before?.self_doubt ?? 6,
    };
    const fullResAfter: EmotionalResidue = {
      shame: draft.emotional_residue_after?.shame ?? 4,
      anxiety: draft.emotional_residue_after?.anxiety ?? 5,
      fatigue: draft.emotional_residue_after?.fatigue ?? 6,
      self_doubt: draft.emotional_residue_after?.self_doubt ?? 5,
    };

    const rehearsal = saveRehearsalLocally({
      gratitude: (draft.gratitude || '').trim(),
      scenario: draft.scenario?.trim(),
      rumination_theme: draft.rumination_theme || 'other',
      emotional_residue_before: fullResBefore,
      emotional_residue_after: fullResAfter,
      reappraisal_notes: draft.reappraisal_notes?.trim(),
      outcome_type: draft.outcome_type!,
      outcome_text: (draft.outcome_text || '').trim(),
      tolerance_before: draft.tolerance_before ?? 4,
      tolerance_after: draft.tolerance_after ?? 5,
      reappraisal_success: draft.reappraisal_success ?? 5,
      repeat_loop_frequency: draft.repeat_loop_frequency || 'weekly',
      duration_ms: duration,
    });

    clearDraft();

    // Attempt server if user chose and consent allows
    if (alsoServer && consent.allow_server_storage) {
      await syncRehearsalToServer(rehearsal, consent);
    }

    setIsSaving(false);
    onComplete(rehearsal.id);
  }

  // --- Render helpers for calm sliders ---
  function ResidueSliders(_which: 'before' | 'after', value: Partial<EmotionalResidue>, onChange: (v: Partial<EmotionalResidue>) => void) {
    const labels = [
      { key: 'shame' as const, label: 'Shame / embarrassment' },
      { key: 'anxiety' as const, label: 'Anxiety / dread' },
      { key: 'fatigue' as const, label: 'Emotional fatigue / depletion' },
      { key: 'self_doubt' as const, label: 'Self-doubt about competence' },
    ];
    return (
      <div className="space-y-6">
        {labels.map(({ key, label }) => {
          const v = value[key] ?? 5;
          return (
            <div key={key}>
              <div className="flex justify-between text-sm mb-1.5">
                <div>{label}</div>
                <div className="tabular-nums text-[var(--text-muted)]">{v}</div>
              </div>
              <input
                type="range"
                min={0}
                max={10}
                step={1}
                value={v}
                onChange={(e) => onChange({ ...value, [key]: parseInt(e.target.value) })}
                className="w-full accent-[var(--accent)]"
              />
              <div className="flex justify-between text-[10px] text-[var(--text-subtle)] mt-0.5">
                <div>barely present</div>
                <div>overwhelming</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="min-h-[100svh] bg-[var(--bg)] text-[var(--text)]">
      <div className="max-w-[760px] mx-auto px-5 pt-8 pb-24">
        {/* Quiet header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={onExit} className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text)]">
            <ArrowLeft size={16} /> Exit rehearsal
          </button>
          <div className="text-xs text-[var(--text-subtle)] tabular-nums">
            {stepIndex + 1} / {STEPS.length} — take your time
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* STEP 1: Complicated Gratitude */}
          {step === 'gratitude' && (
            <motion.div key="g" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="mb-8">
                <div className="text-[11px] tracking-[2px] text-[var(--accent)] mb-2">THE BEGINNING</div>
                <h2 className="text-3xl font-medium tracking-tight">Complicated gratitude</h2>
                <p className="mt-3 text-[var(--text-muted)] max-w-prose">
                  The fear of being wrong in public often contains something protective — care for the work, standards, reputation, colleagues.
                  Naming what is still worth being grateful for, even inside the dread, can make the fear slightly less total.
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="text-sm mb-2 text-[var(--text-muted)]">What part of this fear feels like it is trying to protect something you value?</div>
                  <textarea
                    value={draft.gratitude || ''}
                    onChange={(e) => updateDraft({ gratitude: e.target.value })}
                    placeholder="I am grateful that my fear of being wrong protects my..."
                    className="w-full min-h-[108px] rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] p-5 text-[15px] leading-relaxed placeholder:text-[var(--text-subtle)] focus:outline-none focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <div className="text-sm mb-2 text-[var(--text-muted)]">What has carrying this fear been costing you (energy, sleep, presence with people you care about)?</div>
                  <textarea
                    value={draft.scenario || ''}
                    onChange={(e) => updateDraft({ scenario: e.target.value })}
                    placeholder="It has been costing me..."
                    className="w-full min-h-[92px] rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] p-5 text-[15px] leading-relaxed placeholder:text-[var(--text-subtle)] focus:outline-none focus:border-[var(--accent)]"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Name the loop */}
          {step === 'rumination' && (
            <motion.div key="r" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="mb-8">
                <h2 className="text-3xl font-medium tracking-tight">Name the loop</h2>
                <p className="mt-3 text-[var(--text-muted)]">Most of us have one dominant flavor of rumination when this fear is active. Naming it reduces its shapeless power.</p>
              </div>

              <div className="grid gap-2">
                {(Object.keys(RUMINATION_LABELS) as (RuminationTheme | 'other')[]).map((key) => (
                  <button
                    key={key}
                    onClick={() => updateDraft({ rumination_theme: key })}
                    className={`text-left rounded-2xl border px-5 py-4 transition ${draft.rumination_theme === key ? 'border-[var(--accent)] bg-[var(--accent)]/5' : 'border-[var(--border)] hover:border-[var(--text-subtle)]'}`}
                  >
                    {RUMINATION_LABELS[key]}
                  </button>
                ))}
              </div>

              {draft.rumination_theme === 'other' && (
                <div className="mt-4">
                  <textarea
                    placeholder="Describe the particular flavor of the loop..."
                    value={(draft.rumination_theme as string) === 'other' ? '' : (draft.rumination_theme as string || '')}
                    onChange={(e) => updateDraft({ rumination_theme: e.target.value })}
                    className="w-full min-h-[90px] rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] p-5"
                  />
                </div>
              )}

              <div className="mt-8 text-xs text-[var(--text-subtle)]">This is for your curve and your future check-ins. It is not used to label you.</div>
            </motion.div>
          )}

          {/* STEP 3: The Courage Rehearsal Stage — emotion regulation container */}
          {step === 'stage' && (
            <motion.div key="s" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="mb-6">
                <div className="inline-block rounded-full bg-[var(--accent)]/10 text-[var(--accent)] text-xs px-3 py-1 tracking-widest mb-3">COURAGE REHEARSAL STAGE</div>
                <h2 className="text-3xl font-medium tracking-tight">Rehearse being wrong</h2>
                <p className="mt-2 text-[var(--text-muted)]">This is the heart of the practice. Slow, private, imaginal exposure + deliberate reappraisal. No audience. No score.</p>
              </div>

              <div className="space-y-8">
                <div className="rounded-3xl border border-[var(--border)] p-6 bg-[var(--bg-elev)]">
                  <div className="uppercase text-xs tracking-[1.5px] text-[var(--text-subtle)] mb-2">1. Locate</div>
                  <p className="text-[15px]">Close your eyes for a moment if it helps. Where in your body do you feel the fear or the replay right now? Name the sensation without trying to make it go away.</p>
                  <div className="mt-4">
                    <div className="text-sm text-[var(--text-muted)] mb-1.5">Current emotional residue (before any reappraisal)</div>
                    {ResidueSliders('before', draft.emotional_residue_before || INITIAL_RESIDUE, (v) => updateDraft({ emotional_residue_before: { ...(draft.emotional_residue_before || {}), ...v } }))}
                  </div>
                </div>

                <div className="rounded-3xl border border-[var(--border)] p-6">
                  <div className="uppercase text-xs tracking-[1.5px] text-[var(--text-subtle)] mb-2">2. Allow</div>
                  <p className="text-[15px]">The fear is information that you care. Let the feeling be here for thirty seconds without arguing with it or fixing it. You are not the fear; you are the one who can hold it.</p>
                </div>

                <div className="rounded-3xl border border-[var(--border)] p-6">
                  <div className="uppercase text-xs tracking-[1.5px] text-[var(--text-subtle)] mb-2">3. Reappraise (gently)</div>
                  <div className="space-y-4 text-[15px] text-[var(--text-muted)]">
                    <p>What is the story the fear is telling you right now? Write the catastrophic version briefly.</p>
                    <textarea
                      value={draft.reappraisal_notes || ''}
                      onChange={(e) => updateDraft({ reappraisal_notes: e.target.value })}
                      placeholder="The story is that if I am seen as wrong then..."
                      className="w-full min-h-[82px] rounded-2xl border border-[var(--border)] bg-white/50 dark:bg-black/10 p-4"
                    />
                    <p className="pt-2">Now the smaller, truer, or more survivable version:</p>
                    <textarea
                      value={draft.reappraisal_notes || ''}
                      onChange={(e) => updateDraft({ reappraisal_notes: e.target.value })}
                      placeholder="Even if that happened, the realistic next step would be..."
                      className="w-full min-h-[82px] rounded-2xl border border-[var(--border)] bg-white/50 dark:bg-black/10 p-4"
                    />
                  </div>
                </div>

                <div className="text-xs text-[var(--text-subtle)] pl-1">You do not need to “win” against the fear here. The goal is to have practiced sitting with the possibility of being seen as wrong, on your own terms.</div>
              </div>
            </motion.div>
          )}

          {/* STEP 4: The ending — better question / choice / repair */}
          {step === 'outcome' && (
            <motion.div key="o" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="mb-8">
                <h2 className="text-3xl font-medium tracking-tight">End with a move</h2>
                <p className="mt-3 text-[var(--text-muted)]">The rehearsal is not complete until you have a concrete, usable alternative to the old loop. This is what you take back into the world.</p>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                {(['question', 'choice', 'repair'] as OutcomeType[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => updateDraft({ outcome_type: t })}
                    className={`rounded-2xl border p-5 text-left transition ${draft.outcome_type === t ? 'border-[var(--accent)] bg-[var(--accent)]/5' : 'border-[var(--border)] hover:border-[var(--text-subtle)]'}`}
                  >
                    <div className="font-medium mb-1">{OUTCOME_LABELS[t]}</div>
                    <div className="text-xs text-[var(--text-subtle)]">
                      {t === 'question' && 'Something you can actually say out loud or ask yourself.'}
                      {t === 'choice' && 'A small, doable action or boundary in the moment.'}
                      {t === 'repair' && 'Language or action for after the fact.'}
                    </div>
                  </button>
                ))}
              </div>

              <div>
                <div className="text-sm mb-2 text-[var(--text-muted)]">Write the actual words or action you want to remember or practice.</div>
                <textarea
                  value={draft.outcome_text || ''}
                  onChange={(e) => updateDraft({ outcome_text: e.target.value })}
                  placeholder="I will say: “I may be missing something here — can we look at the data together?”"
                  className="w-full min-h-[120px] rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] p-5 text-[15px] leading-relaxed"
                />
              </div>
            </motion.div>
          )}

          {/* STEP 5: Post-rehearsal measures — the data that builds the curve */}
          {step === 'measures' && (
            <motion.div key="m" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="mb-8">
                <h2 className="text-3xl font-medium tracking-tight">After the stage</h2>
                <p className="mt-3 text-[var(--text-muted)]">These numbers are the raw material of your ambiguity tolerance curve. They are honest snapshots, not performance metrics.</p>
              </div>

              <div className="space-y-8">
                <div>
                  <div className="text-sm mb-2">Right now, how well can you sit with the possibility of being seen as wrong in public? (1 = impossible, 10 = I can hold it)</div>
                  <input type="range" min={1} max={10} value={draft.tolerance_after ?? 5} onChange={(e) => updateDraft({ tolerance_after: parseInt(e.target.value) })} className="w-full accent-[var(--accent)]" />
                  <div className="flex justify-between text-xs text-[var(--text-subtle)]"><div>1</div><div>10</div></div>
                </div>

                <div>
                  <div className="text-sm mb-2">How much did this rehearsal move the charge for you, right now? (0 = none, 10 = significantly lighter)</div>
                  <input type="range" min={0} max={10} value={draft.reappraisal_success ?? 5} onChange={(e) => updateDraft({ reappraisal_success: parseInt(e.target.value) })} className="w-full accent-[var(--accent)]" />
                  <div className="flex justify-between text-xs text-[var(--text-subtle)]"><div>0</div><div>10</div></div>
                </div>

                <div>
                  <div className="text-sm mb-2">How often does this particular loop visit you uninvited?</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(Object.keys(FREQUENCY_LABELS) as LoopFrequency[]).map((f) => (
                      <button key={f} onClick={() => updateDraft({ repeat_loop_frequency: f })} className={`text-left text-sm rounded-xl border px-4 py-3 ${draft.repeat_loop_frequency === f ? 'border-[var(--accent)]' : 'border-[var(--border)]'}`}>
                        {FREQUENCY_LABELS[f]}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-sm mb-2 text-[var(--text-muted)]">Emotional residue now (after the rehearsal)</div>
                  {ResidueSliders('after', draft.emotional_residue_after || INITIAL_RESIDUE, (v) => updateDraft({ emotional_residue_after: { ...(draft.emotional_residue_after || {}), ...v } }))}
                </div>
              </div>
            </motion.div>
          )}

          {/* FINAL REVIEW + CONSENTED SAVE */}
          {step === 'review' && (
            <motion.div key="rev" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="mb-8">
                <h2 className="text-3xl font-medium tracking-tight">Before we save</h2>
                <p className="mt-2 text-[var(--text-muted)]">You can keep everything locally and still see your curve in this browser. Cloud storage is only for cross-device continuity and the follow-up prompts.</p>
              </div>

              <div className="rounded-3xl border border-[var(--border)] p-6 mb-6 bg-[var(--bg-elev)] text-sm">
                <div className="font-medium mb-3">Summary of this rehearsal</div>
                <div className="space-y-1 text-[var(--text-muted)]">
                  <div><span className="text-[var(--text)]">Theme:</span> {RUMINATION_LABELS[draft.rumination_theme as RuminationTheme] || draft.rumination_theme}</div>
                  <div><span className="text-[var(--text)]">Outcome:</span> {draft.outcome_type && OUTCOME_LABELS[draft.outcome_type]} — “{draft.outcome_text?.slice(0, 90)}{(draft.outcome_text || '').length > 90 ? '…' : ''}”</div>
                  <div><span className="text-[var(--text)]">Tolerance shift:</span> {draft.tolerance_before ?? 4} → {draft.tolerance_after}</div>
                  <div><span className="text-[var(--text)]">Reappraisal success:</span> {draft.reappraisal_success}</div>
                </div>
              </div>

              <div className="space-y-3 text-sm mb-8">
                <label className="flex items-start gap-3">
                  <input type="checkbox" checked={localOnly} onChange={(e) => setLocalOnly(e.target.checked)} className="mt-1" />
                  <span>Save only on this device (local curve only). I can still export everything later.</span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={!localOnly && consent.allow_server_storage}
                    onChange={(e) => setLocalOnly(!e.target.checked)}
                    disabled={!consent.allow_server_storage}
                    className="mt-1"
                  />
                  <span className={!consent.allow_server_storage ? 'opacity-50' : ''}>
                    Also store on the server under my workspace ID so the curve and any future check-ins are available across devices. I can delete at any time.
                  </span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => finishAndSave(!localOnly)}
                  disabled={isSaving}
                  className="flex-1 rounded-2xl bg-[var(--accent)] disabled:opacity-60 text-white py-3.5 font-medium flex items-center justify-center gap-2"
                >
                  {isSaving ? 'Saving…' : 'Save this rehearsal to my curve'}
                  <Check size={18} />
                </button>
                <button onClick={onExit} className="flex-1 rounded-2xl border py-3.5 border-[var(--border)]">Discard and exit</button>
              </div>

              <p className="text-center text-xs text-[var(--text-subtle)] mt-5">You can always come back and add a check-in manually from the dashboard.</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation — calm, no pressure */}
        {step !== 'review' && (
          <div className="mt-10 flex items-center justify-between">
            <button onClick={back} disabled={stepIndex === 0} className="inline-flex items-center gap-2 text-sm disabled:opacity-40">
              <ArrowLeft size={16} /> Back
            </button>
            <button
              onClick={next}
              disabled={!canAdvance}
              className="inline-flex items-center gap-2 rounded-2xl border px-6 py-2.5 text-sm disabled:opacity-40 hover:bg-[var(--bg-elev)] transition"
            >
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
